<template>
  <div class="my-3">
    <div :class="{ 'fixed-main-box': !modalLayout }">
      <b-row>
        <b-col cols="12" sm="6">
          <h4><b>{{ $t('campaign-admin.titles.job-selection') }}</b></h4>
          <p>{{ $t('campaign-admin.hints.job-selection-2') }}</p>
          <advanced-form :form="formFields" v-bind="form" v-model="form" :hideFormControl="true" />
          <hr />
          <b-alert class="mb-3" show :variant="totalInSelection ? 'outline-primary' : 'outline-secondary'">
            <strong>{{ totalInSelection | plural('job') }} {{ $t('campaign-admin.labels.in-selection') }}</strong>
          </b-alert>
          <p>{{ $t('campaign-admin.hints.job-selection') }}</p>
        </b-col>
        <b-col cols="12" sm="6" class="border-left">
          <div>
            <b-input v-model="searchTerm" :placeholder="$t('campaign-admin.placeholders.search-job')" />
          </div>
          <hr class="d-sm-none"/>
          <b-list-group v-if="Array.isArray(jobs) && jobs.length" class="my-3">
            <b-list-group-item
              v-for="({ entry }) in jobs"
              :key="entry.id"
              id="jobs-list"
              :style="{
                'background-color': notIDs.includes(entry.id) ? '#f3f4f6' : 'white',
                'color': notIDs.includes(entry.id) ? '#6b7280' : 'black',
              }"
            >
              <div class="d-flex justify-content-between align-items-center">
                <span class="m-0" v-b-modal="`description-dept-${entry.id}`">
                  {{ truncateJobTitle(entry.title) }}
                  <div v-if="(isEXPD8 || isWickes) && entry.customFieldsHash && entry.customFieldsHash.location">
                    <small>{{ entry.customFieldsHash.location }}</small>
                  </div>
                </span>

                <div>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="toggleExcludeJob(entry.id)"
                  >{{ notIDs.includes(entry.id) ? 'Include' : 'Exclude' }}</b-button>

                  <b-button
                     v-if="!modalLayout"
                     class="ml-2"
                     size="sm"
                    :variant="dashboardJobIDs.includes(entry.id) ? 'primary' : 'outline-primary'"
                    @click="toggleJobToDashboard(entry.id)"
                  >
                    <font-awesome-icon icon="thumbtack" />
                  </b-button>
                </div>

                <!-- Job Description Modal -->
                <b-modal
                  :id="`description-dept-${entry.id}`"
                  :title="entry.title"
                  hide-footer
                  lazy
                  size="lg"
                >
                  <key-stats class="mb-3 w-100" :job="entry"/>
                  <markdown :source="entry.description" :anchorAttributes="markdownAnchorAttributes"/>
                </b-modal>
              </div>
            </b-list-group-item>
          </b-list-group>

          <div v-else class="p-4 text-center">{{ $t('campaign-admin.labels.no-jobs-found') }}</div>

          <!-- Pagination -->
          <div class="d-flex justify-content-center">
            <b-pagination v-model="page" :total-rows="total" :per-page="limit" aria-controls="jobs-list" />
          </div>
        </b-col>
      </b-row>
    </div>
    <div
      class="d-flex flex-row justify-content-start align-items-center" :class="{ 'fixed-buttons-box': !modalLayout }" cols="12">
      <b-btn v-if="!modalLayout" @click="back" variant="transparent">{{ $t('campaign.scheduled-posts.back') }}</b-btn>
      <div class="flex-fill"/>
      <b-btn class="mr-2" @click="saveSegment" variant="primary" pill>{{ $t('campaign-admin.buttons.save-filters') }}</b-btn>
    </div>
  </div>
</template>

<script>
import _isEqual from 'lodash/isEqual';
import CampaignAdminJobsSegmentQuery from '@/graphql/queries/CampaignAdminJobsSegment.gql';
import Markdown from 'vue-markdown';
import KeyStats from '@src/components/jobs/key-stats';
import AdvancedForm from '@src/components/AdvancedForm';
import { mapRoot } from '@src/mixins/root';

export default {
  name: 'CampaignAdminJobsSegment',
  props: {
    campaign: {
      type: Object,
    },
    value: {
      type: Object,
    },
    modalLayout: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Markdown,
    KeyStats,
    AdvancedForm
  },
  data: () => ({
    searchTerm: null,
    page: 1,
    limit: 10,
    form: {},
    notIDs: [],
    dashboardJobIDs: [],
    initialReturnSegment: null
  }),
  apollo: {
    api: {
      query: CampaignAdminJobsSegmentQuery,
      variables() {
        const { segment, searchTerm, limit, page } = this;
        const whereJobs = {
          ...(segment || {}),
          ...(searchTerm && searchTerm.length > 1 ? { title: searchTerm } : {}),
          status: 'ACTIVE',
        };
        return { whereJobs, limit, page };
      },
      update({ company }) {
        return company;
      },
      fetchPolicy: 'network-only'
    },
    totalInSelection: {
      query: CampaignAdminJobsSegmentQuery,
      variables() {
        const { returnSegment } = this;
        const whereJobs = { ...(returnSegment || {}), status: 'ACTIVE' };
        return { whereJobs, limit: 1, page: 1 };
      },
      update({ company }) {
        const { jobConnections } = company || {};
        const { total } = jobConnections || {};
        return total || 0;
      },
      fetchPolicy: 'network-only'
    },
  },
  created() {
    // Set segment form
    const { value } = this;
    if (!value) return;
    const { AND } = value;
    if (!Array.isArray(AND) || !AND.length) return;
    this.form = AND.reduce((r, { departments, NOT, OR }) => {
      if (departments) {
        const { id_in: idIn } = departments;
        r.departments = idIn;
        return r;
      } else if (NOT && NOT.length) {
        const { id_in: idIn } = NOT[0];
        this.notIDs = idIn;
        return r;
      } else if (Array.isArray(OR) && OR.length && OR[0].customFields &&
        Array.isArray(OR[0].customFields) && OR[0].customFields.length) {
        if (OR[0].customFields[0].key === 'location') {
          r.locations = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'location' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'brands') {
          r.brands = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'brands' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'region') {
          r.regions = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'region' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'district') {
          r.districts = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'district' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'city') {
          r.cities = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'city' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'country') {
          r.countries = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'country' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'postal-district') {
          r.postalDistricts = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'postal-district' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'job-title') {
          r.jobTitles = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'job-title' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'activity') {
          r.activities = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'activity' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'chain') {
          r.chains = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'chain' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'area') {
          r.areas = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'area' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'company') {
          r.companies = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'company' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'job-position') {
          r.jobPositions = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'job-position' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'call-center') {
          r.callCenters = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'call-center' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'office') {
          r.offices = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'office' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'organisation') {
          r.organisations = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'organisation' ? customField.value : null;
          }).filter(el => el);
          return r;
        } else if (OR[0].customFields[0].key === 'reference-number') {
          r.referenceNumbers = OR.map(({ customFields }) => {
            const [ customField ] = customFields;
            return customField.key === 'reference-number' ? customField.value : null;
          }).filter(el => el);
          return r;
        }
      }
    }, {});
    const { dashboardJobs } = this.campaign || {};
    this.dashboardJobIDs = (Array.isArray(dashboardJobs) ? dashboardJobs : []).map(({ id }) => id);

    setTimeout(() => {
      this.initialReturnSegment = JSON.parse(JSON.stringify(this.returnSegment || {}));
    }, 500);
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isRecruiter', 'isRecruiterAdmin', 'isProd', 'isEXPD8', 'isChalhoub', 'isWickes' ]),
    unsavedChanges() {
      return !_isEqual(this.initialReturnSegment, this.returnSegment);
    },
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    jobs() {
      const { jobConnections } = this.api || {};
      const { edges } = jobConnections || {};
      return edges || [];
    },
    total() {
      const { jobConnections } = this.api || {};
      return jobConnections && jobConnections.total ? jobConnections.total : 0;
    },
    segment() {
      const { form } = this;
      const {
        activities,
        areas,
        brands,
        callCenters,
        chains,
        cities,
        companies,
        countries,
        departments,
        districts,
        jobPositions,
        jobTitles,
        locations,
        offices,
        organisations,
        postalDistricts,
        regions,
        referenceNumbers
      } = form || {};

      if ((!Array.isArray(departments) || !departments.length) &&
        (!Array.isArray(locations) || !locations.length) &&
        (!Array.isArray(brands) || !brands.length) &&
        (!Array.isArray(regions) || !regions.length) &&
        (!Array.isArray(districts) || !districts.length) &&
        (!Array.isArray(cities) || !cities.length) &&
        (!Array.isArray(countries) || !countries.length) &&
        (!Array.isArray(postalDistricts) || !postalDistricts.length) &&
        (!Array.isArray(jobTitles) || !jobTitles.length) &&
        (!Array.isArray(activities) || !activities.length) &&
        (!Array.isArray(chains) || !chains.length) &&
        (!Array.isArray(areas) || !areas.length) &&
        (!Array.isArray(companies) || !companies.length) &&
        (!Array.isArray(jobPositions) || !jobPositions.length) &&
        (!Array.isArray(callCenters) || !callCenters.length) &&
        (!Array.isArray(offices) || !offices.length) &&
        (!Array.isArray(organisations) || !organisations.length) &&
        (!Array.isArray(referenceNumbers) || !referenceNumbers.length)) {
        return null;
      }

      const AND = [];
      if (Array.isArray(departments) && departments.length) {
        AND.push({ departments: { id_in: departments } });
      }
      if (Array.isArray(locations) && locations.length) {
        AND.push({ OR: locations.map(value => ({ customFields: [ { key: 'location', value } ] })) });
      }
      if (Array.isArray(brands) && brands.length) {
        AND.push({ OR: brands.map(value => ({ customFields: [ { key: 'brands', value } ] })) });
      }
      if (Array.isArray(regions) && regions.length) {
        AND.push({ OR: regions.map(value => ({ customFields: [ { key: 'region', value } ] })) });
      }
      if (Array.isArray(districts) && districts.length) {
        AND.push({ OR: districts.map(value => ({ customFields: [ { key: 'district', value } ] })) });
      }
      if (!this.isChalhoub && Array.isArray(cities) && cities.length) {
        AND.push({ OR: cities.map(value => ({ customFields: [ { key: 'city', value } ] })) });
      }
      if (Array.isArray(countries) && countries.length) {
        AND.push({ OR: countries.map(value => ({ customFields: [ { key: 'country', value } ] })) });
      }
      if (Array.isArray(postalDistricts) && postalDistricts.length) {
        AND.push({ OR: postalDistricts.map(value => ({ customFields: [ { key: 'postal-district', value } ] })) });
      }
      if (Array.isArray(jobTitles) && jobTitles.length) {
        AND.push({ OR: jobTitles.map(value => ({ customFields: [ { key: 'job-title', value } ] })) });
      }
      if (Array.isArray(activities) && activities.length) {
        AND.push({ OR: activities.map(value => ({ customFields: [ { key: 'activity', value } ] })) });
      }
      if (Array.isArray(chains) && chains.length) {
        AND.push({ OR: chains.map(value => ({ customFields: [ { key: 'chain', value } ] })) });
      }
      if (Array.isArray(areas) && areas.length) {
        AND.push({ OR: areas.map(value => ({ customFields: [ { key: 'area', value } ] })) });
      }
      if (Array.isArray(companies) && companies.length) {
        AND.push({ OR: companies.map(value => ({ customFields: [ { key: 'company', value } ] })) });
      }
      if (Array.isArray(jobPositions) && jobPositions.length) {
        AND.push({ OR: jobPositions.map(value => ({ customFields: [ { key: 'job-position', value } ] })) });
      }
      if (Array.isArray(callCenters) && callCenters.length) {
        AND.push({ OR: callCenters.map(value => ({ customFields: [ { key: 'call-center', value } ] })) });
      }
      if (Array.isArray(offices) && offices.length) {
        AND.push({ OR: offices.map(value => ({ customFields: [ { key: 'office', value } ] })) });
      }
      if (Array.isArray(organisations) && organisations.length) {
        AND.push({ OR: organisations.map(value => ({ customFields: [ { key: 'organisation', value } ] })) });
      }
      if (Array.isArray(referenceNumbers) && referenceNumbers.length) {
        AND.push({ OR: referenceNumbers.map(value => ({ customFields: [ { key: 'reference-number', value } ] })) });
      }

      return { AND };
    },
    returnSegment() {
      const { segment, notIDs } = this;
      if (!segment && !(Array.isArray(notIDs) || notIDs.length)) return null;
      const result = JSON.parse(JSON.stringify(segment)) || {};
      if (notIDs.length && Array.isArray(result.AND)) {
        result.AND.push({ NOT: [ { id_in: notIDs } ] });
      } else if (notIDs.length) {
        result.AND = { NOT: [ { id_in: notIDs } ] };
      }
      return result;
    },
    formFields() {
      const { departments, jobAggs } = this.api || {};
      const {
        activity,
        area,
        brand,
        callCenter,
        chain,
        city,
        company,
        country,
        district,
        jobPosition,
        jobTitle,
        location,
        office,
        organisation,
        postalDistrict,
        region,
        referenceNumber,
      } = jobAggs || {};
      return {
        ...(!this.isEXPD8 && Array.isArray(departments) && departments.length ? {
          departments: {
            label: this.$t('campaign-admin.labels.departments'),
            type: 'MULTISELECT',
            choices: departments.reduce((r, { id, name }) => ({ ...r, [id]: name }), {}),
          },
        } : {}),
        ...(!this.isEXPD8 && Array.isArray(location) && location.length ? {
          locations: {
            label: this.$t('campaign-admin.labels.locations'),
            type: 'MULTISELECT',
            choices: location.reduce((r, el) => ({ ...r, [el]: el }), {}),
          },
        } : {}),
        ...(Array.isArray(brand) && brand.length ? {
          brands: {
            label: this.$t('campaign-admin.labels.brands'),
            type: 'MULTISELECT',
            choices: brand.reduce((r, el) => ({ ...r, [el]: el }), {}),
          },
        } : {}),
        ...(Array.isArray(region) && region.length ? {
          regions: {
            label: this.$t('campaign-admin.labels.regions'),
            type: 'MULTISELECT',
            choices: region.reduce((r, el) => ({ ...r, [el]: el }), {}),
          },
        } : {}),
        ...(Array.isArray(district) && district.length ? {
          districts: {
            label: this.$t('campaign-admin.labels.districts'),
            type: 'MULTISELECT',
            choices: district.reduce((r, el) => ({ ...r, [el]: el }), {}),
          },
        } : {}),
        ...(!this.isChalhoub && Array.isArray(city) && city.length ? {
          cities: {
            label: this.$t('campaign-admin.labels.cities'),
            type: 'MULTISELECT',
            choices: city.reduce((r, el) => ({ ...r, [el]: el }), {}),
          },
        } : {}),
        ...(Array.isArray(country) && country.length ? {
          countries: {
            label: this.$t('campaign-admin.labels.countries'),
            type: 'MULTISELECT',
            choices: country.reduce((r, el) => ({ ...r, [el]: el }), {}),
          },
        } : {}),
        ...(Array.isArray(postalDistrict) && postalDistrict.length ? {
          postalDistricts: {
            label: this.$t('campaign-admin.labels.postalDistricts'),
            type: 'MULTISELECT',
            choices: postalDistrict.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(jobTitle) && jobTitle.length ? {
          jobTitles: {
            label: this.$t('campaign-admin.labels.titles'),
            type: 'MULTISELECT',
            choices: jobTitle.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(activity) && activity.length ? {
          activities: {
            label: this.$t('campaign-admin.labels.activities'),
            type: 'MULTISELECT',
            choices: activity.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(chain) && chain.length ? {
          chains: {
            label: this.$t('campaign-admin.labels.chains'),
            type: 'MULTISELECT',
            choices: chain.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(area) && area.length ? {
          areas: {
            label: this.$t('campaign-admin.labels.areas'),
            type: 'MULTISELECT',
            choices: area.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(company) && company.length ? {
          companies: {
            label: this.$t('campaign-admin.labels.companies'),
            type: 'MULTISELECT',
            choices: company.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(jobPosition) && jobPosition.length ? {
          jobPositions: {
            label: this.$t('campaign-admin.labels.jobPositions'),
            type: 'MULTISELECT',
            choices: jobPosition.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(callCenter) && callCenter.length ? {
          callCenters: {
            label: this.$t('campaign-admin.labels.callCenters'),
            type: 'MULTISELECT',
            choices: callCenter.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(office) && office.length ? {
          offices: {
            label: this.$t('campaign-admin.labels.offices'),
            type: 'MULTISELECT',
            choices: office.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(organisation) && organisation.length ? {
          organisations: {
            label: this.$t('campaign-admin.labels.organisations'),
            type: 'MULTISELECT',
            choices: organisation.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
        ...(Array.isArray(referenceNumber) && referenceNumber.length ? {
          referenceNumbers: {
            label: this.$t('campaign-admin.labels.referenceNumbers'),
            type: 'MULTISELECT',
            choices: referenceNumber.reduce((r, el) => ({ ...r, [el]: el }), {}),
          }
        } : {}),
      };
    }
  },
  methods: {
    truncateJobTitle(jobTitle) {
      if (jobTitle.length < 40) {
        return jobTitle;
      } else {
        return jobTitle.substring(0, 40).concat('...');
      }
    },
    toggleExcludeJob(jobID) {
      const { notIDs } = this;
      if (notIDs.includes(jobID)) {
        this.notIDs = notIDs.filter(el => el !== jobID);
      } else {
        this.notIDs = [ ...notIDs, jobID ];
      }
    },
    toggleJobToDashboard(jobID) {
      const { dashboardJobIDs } = this;
      if (dashboardJobIDs.includes(jobID)) {
        this.dashboardJobIDs = dashboardJobIDs.filter(el => el !== jobID);
      } else {
        this.dashboardJobIDs = [ ...dashboardJobIDs, jobID ];
      }
      this.$emit('toggleJobToDashboard', this.dashboardJobIDs);
    },
    saveSegment() {
      const { returnSegment } = this;
      this.initialReturnSegment = JSON.parse(JSON.stringify(this.returnSegment || {}));
      this.$emit('saveSegment', returnSegment);
    },
    back() {
      this.$emit('back');
    },
  },
};
</script>
<style lang="scss" scoped>
  .inactiveButton {
    color: #001DC0;
    text-decoration: underline;
  }
  .activeButton {
    color: white;
    background-color: #001DC0;
  }
  .activeButton:hover {
    background-color: red;
    color: white;
  }
  .inactiveButton:hover {
    color: white;
    background-color: #001DC0;
  }
  .hidden {
    visibility: hidden;
  }
  .link-styling {
    text-decoration: underline;
    cursor: pointer;
  }
  .fixed-buttons-box {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: white;
    padding: 8px 10px 8px 10px;
    border-top: 1px solid #e9ecef;
    z-index: 1000;
  }
  .fixed-main-box {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 60px;
    padding: 20px;
    overflow-y: scroll;
  }
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-job-segment.vue